/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/DirectLogin.tsx
 * Release: v2.0.0 - Login diretto per TestSprite e testing
 * Data: 2025-08-27
 * 
 * Questo componente fornisce un accesso diretto all'applicazione
 * bypassando completamente il video introduttivo e le animazioni.
 * È ideale per testing automatizzato e accesso rapido.
 */

import React, { useState } from 'react';
import { useAuth } from './AuthProvider';
import coronatekLogo from './asset/Coronatek_Logo_bordato_92dpi_200x88_1.webp';
import dialogicomLogo from './asset/Dialogicom_432x144.webp';
import GoogleSignInButton from './GoogleSignInButton';

const DirectLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthOnly, setOauthOnly] = useState<null | { provider: string }>(null);
  
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setOauthOnly(null);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        console.log('✅ Login diretto completato con successo');
        // Redirect immediato alla dashboard
        window.location.replace('/dashboard');
        return;
      } else {
        console.error('❌ Errore login diretto:', result.error);
        setError(result.error || 'Credenziali non valide');
        
        // Gestione OAuth only
        try {
          const parsed = result as any;
          if (parsed.oauth_only && parsed.provider === 'google') {
            setOauthOnly({ provider: 'google' });
          }
        } catch {}
      }
    } catch (error) {
      console.error('Errore login diretto:', error);
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header con logo */}
        <div className="text-center mb-8">
          <img 
            src={coronatekLogo} 
            alt="Coronatek Logo" 
            className="mx-auto h-16 mb-4 drop-shadow-lg" 
          />
          <h1 className="text-3xl font-bold text-white mb-2">
            <span className="text-green-400">Turbo</span>Manager
          </h1>
          <p className="text-gray-400 text-sm">
            Accesso diretto - Modalità Testing
          </p>
        </div>

        {/* Form di login */}
        <div className="bg-slate-800 rounded-xl shadow-2xl p-6 border border-slate-600">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder="Inserisci la tua email"
                disabled={isLoading}
              />
            </div>

            {/* Campo Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder="Inserisci la tua password"
                disabled={isLoading}
              />
            </div>

            {/* Messaggio di errore */}
            {error && (
              <div className="bg-red-900/50 border border-red-500 rounded-lg p-3 text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Messaggio OAuth only */}
            {oauthOnly && (
              <div className="bg-blue-900/50 border border-blue-500 rounded-lg p-3 text-blue-200 text-sm">
                Hai effettuato la registrazione con Google. Usa il pulsante "Continua con Google".
              </div>
            )}

            {/* Pulsante di login */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Accesso in corso...
                </div>
              ) : (
                'Accedi'
              )}
            </button>
          </form>

          {/* Separatore */}
          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-slate-600"></div>
            <span className="px-3 text-sm text-gray-400">oppure</span>
            <div className="flex-1 border-t border-slate-600"></div>
          </div>

          {/* Login con Google */}
          <GoogleSignInButton />

          {/* Link di navigazione */}
          <div className="mt-6 text-center space-y-2">
            <a 
              href="/login" 
              className="block text-sm text-gray-400 hover:text-white transition-colors duration-200"
            >
              ← Torna al login con video introduttivo
            </a>
            <a 
              href="/sign-up" 
              className="block text-sm text-gray-400 hover:text-white transition-colors duration-200"
            >
              Non hai un account? Registrati
            </a>
          </div>
        </div>

        {/* Footer Dialogicom */}
        <div className="text-center mt-6">
          <a 
            href="https://dialogicom.com" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="inline-block transition-transform duration-200 hover:scale-105"
          >
            <div className="text-xs text-gray-500 mb-1">Powered by</div>
            <img 
              src={dialogicomLogo} 
              alt="Dialogicom Logo" 
              className="mx-auto w-32 opacity-60 hover:opacity-80 transition-opacity duration-200" 
            />
          </a>
        </div>

        {/* Indicatore modalità testing */}
        <div className="text-center mt-4">
          <span className="inline-block bg-yellow-600 text-yellow-100 text-xs px-2 py-1 rounded-full font-medium">
            🔧 Modalità Testing - Accesso Diretto
          </span>
        </div>
      </div>
    </div>
  );
};

export default DirectLogin;
